import lodash from 'lodash';
import type { ComponentType, FC, PropsWithChildren } from 'react';
import React, { useEffect, useRef, useState } from 'react';

import type { DocumentStyle } from './../../ricos-schema/legacy/wix/rich_content/v1';
import type { CssVarsObject, Environment, RicosTheme } from './../../ricos-types';

import { getStyles } from '../get-styles';
import type { RicosStyles } from '../styles';
import { buildThemeCssVars, buildThemePortalCssVars } from '../themeStrategy/build-theme-css-vars';

export const StylesContext = React.createContext<StylesContextData>({
  styles: null,
  cssVars: {},
  portalCssVars: {},
  setEditorElement: null,
  stylePreset: null,
} as unknown as StylesContextData);

type StylesContextProps = {
  documentStyle: DocumentStyle;
  styles: RicosStyles;
  theme: RicosTheme;
  environment: Environment;
  isMobile: boolean;
};

type StylesContextData = {
  styles: RicosStyles;
  cssVars: CssVarsObject;
  portalCssVars: CssVarsObject;
  setEditorElement: (e: HTMLElement) => void;
  stylePreset: string | null;
};

function usePrevious(value: $TSFixMe) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

const useForceUpdate = () => {
  const [_, setValue] = useState(0);
  return () => setValue(value => value + 1);
};

export const StylesContextProvider: FC<PropsWithChildren<StylesContextProps>> = ({
  children,
  documentStyle,
  theme,
  styles,
  environment,
  isMobile,
}) => {
  const prevTheme = usePrevious(theme);
  const prevDocumentStyle = usePrevious(documentStyle);
  const forceUpdate = useForceUpdate();

  const cssVars = buildThemeCssVars(theme, documentStyle);
  const portalCssVars = buildThemePortalCssVars(theme);

  useEffect(() => {
    const documentStyleSubscription = styles.onDocumentStyleUpdate(forceUpdate);
    const themeSubscription = styles.onThemeUpdate(forceUpdate);
    styles.setTheme(theme).setDocumentStyle(documentStyle);

    return () => {
      documentStyleSubscription.cancel();
      themeSubscription.cancel();
    };
  }, []);

  useEffect(() => {
    if (prevTheme && !lodash.isEqual(prevTheme, theme)) {
      styles.setTheme(theme);
    }
    if (prevDocumentStyle && !lodash.isEqual(prevDocumentStyle, documentStyle)) {
      styles.setDocumentStyle(documentStyle);
    }
  }, [prevTheme, theme, prevDocumentStyle, documentStyle]);

  return (
    <StylesContext.Provider
      value={{
        styles,
        cssVars,
        portalCssVars,
        setEditorElement: styles.setEditorElement,
        stylePreset: getStyles(environment, isMobile),
      }}
    >
      {children}
    </StylesContext.Provider>
  );
};

const StylesContextConsumer = ({ children }: $TSFixMe) => (
  <StylesContext.Consumer>{value => children(value)}</StylesContext.Consumer>
);

export function withStylesContext<Props>(Component: ComponentType<Props>) {
  return (props: Props) => (
    <StylesContextConsumer>
      {(value: StylesContextProps) => <Component {...props} {...value} />}
    </StylesContextConsumer>
  );
}
